import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { useSession } from "../../hooks";
import { useCreateRole, useGetTeams } from "../../http";
import { IRoleAccountability, IUser } from "../../models";
import { CreateEditRoleForm, ICreateEditRoleFormState } from "./CreateEditRoleForm";
import { useEffect } from "react";
import { useParams } from "react-router";

interface UrlParams {
  slug: string;
}

interface IProps {
  visible: boolean;
  onCreated: (guid: string) => void;
  onCancelled: () => void;
}

const CreateRoleModal = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const { userId } = useSession();
  const { mutate, isLoading: isCreatingRole } = useCreateRole();
  const { data: teams } = useGetTeams(undefined, props.visible);

  const { formState, control, handleSubmit, reset, setValue } = useForm<ICreateEditRoleFormState>({
    defaultValues: {
      team: null,
      name: "",
      description: "",
      isOpen: false,
      accountabilities: [] as IRoleAccountability[],
      tags: [],
      members: [] as IUser[],
    },
  });

  useEffect(() => {
    if (teams && props.visible) {
      setValue("team", teams.find((x) => x.slug === slug) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, slug, teams]);

  async function handleAddClicked() {
    handleSubmit((data) => {
      mutate(
        {
          teamSlug: data.team!.slug,
          name: data.name,
          description: data.description,
          isOpen: data.isOpen,
          accountabilities: data.accountabilities,
          tags: data.tags,
          members: data.members.map((x) => x.userId),
          createdByUserId: userId!,
        },
        {
          onSuccess: (data) => {
            reset(undefined, { keepDefaultValues: true });
            props.onCreated(data.guid);
          },
        }
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Create Role</DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <CreateEditRoleForm formState={formState} control={control} loadingEnabled={props.visible} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isCreatingRole} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isCreatingRole}
          endIcon={isCreatingRole ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleAddClicked}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateRoleModal };
